import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageToggle from "../components/app/LanguageToggle";
import AuthImage from "../components/app/AuthImage";
import FavoriteButton from "../components/app/FavoriteButton";
import Logo from "../components/app/Logo";
import { Star, MapPin, Search as SearchIcon, Filter, Sparkles, Trophy, ArrowLeft, Map as MapIcon, LayoutGrid, Users, Building2 } from "lucide-react";

const SPECIALTIES = [
  { id: "football", label_bg: "Футбол", label_en: "Football Coach" },
  { id: "goalkeeper", label_bg: "Вратари", label_en: "Goalkeeper Coach" },
  { id: "fitness", label_bg: "Фитнес", label_en: "Fitness Coach" },
  { id: "individual", label_bg: "Индивидуални", label_en: "Individual Technical Coach" },
  { id: "striker", label_bg: "Атакуващи", label_en: "Striker" },
  { id: "defender", label_bg: "Защитници", label_en: "Defender" },
  { id: "speed", label_bg: "Скорост", label_en: "Speed" },
  { id: "kids", label_bg: "Деца", label_en: "Kids Development" },
  { id: "strength", label_bg: "Сила", label_en: "Strength & Conditioning" },
];

export const SPECIALTY_LABEL = (id, lang) => {
  const s = SPECIALTIES.find((x) => x.id === id);
  if (!s) return id || "";
  return lang === "bg" ? s.label_bg : s.label_en;
};

export { SPECIALTIES };

function CoachCard({ c, lang }) {
  const badgeMeta = (b) => {
    const map = {
      uefa_pro: { label: "UEFA PRO", cls: "gold" },
      uefa_a: { label: "UEFA A", cls: "" },
      uefa_b: { label: "UEFA B", cls: "" },
      verified: { label: lang === "bg" ? "Верифициран" : "Verified", cls: "" },
      top_rated: { label: lang === "bg" ? "Топ" : "Top rated", cls: "amber" },
      elite: { label: "Elite", cls: "platinum" },
    };
    return map[b] || { label: b.replace("_", " "), cls: "" };
  };
  return (
    <Link
      to={`/coach/${c.id}`}
      className="card-coach p-6 group block relative"
      data-testid={`coach-card-${c.id}`}
    >
      {c.is_pro && (
        <div className="absolute top-3 right-3 chip-badge gold badge-pulse z-10">
          <Sparkles className="w-3 h-3" /> PRO
        </div>
      )}
      <div className={`absolute ${c.is_pro ? "top-3 right-[88px]" : "top-3 right-3"} z-10`}>
        <FavoriteButton coachId={c.id} />
      </div>
      <div className="flex items-start gap-5">
        <div className="w-20 h-20 flex-shrink-0 relative">
          {c.photo_url ? (
            <AuthImage src={c.photo_url} className="w-full h-full object-cover rounded-full border-2 border-pitch/40"
              fallback={<div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{c.name?.[0]}</div>} />
          ) : (
            <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-2xl">{c.name?.[0]}</div>
          )}
          {(c.badges || []).includes("verified") && (
            <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-pitch text-white rounded-full flex items-center justify-center text-[11px] font-bold border-2 border-[#0A1628]" title="Verified">✓</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-oswald uppercase text-xl tracking-tight truncate">{c.name}</div>
          <div className="flex items-center gap-2 text-xs mt-1.5">
            <Star className="w-3.5 h-3.5 text-pitch fill-pitch" />
            <span className="font-mono text-white">{(c.rating || 0).toFixed(1)}</span>
            <span className="text-white/25">·</span>
            <span className="font-mono text-white/55">{c.review_count || 0} {lang === "bg" ? "оценки" : "reviews"}</span>
          </div>
          {c.city && (
            <div className="text-xs text-white/50 flex items-center gap-1 mt-1.5 font-mono">
              <MapPin className="w-3 h-3 text-pitch" /> {c.city}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {c.specialty && <span className="chip-badge">{SPECIALTY_LABEL(c.specialty, lang)}</span>}
        {(c.badges || []).slice(0, 3).map((b) => {
          const m = badgeMeta(b);
          return <span key={b} className={`chip-badge ${m.cls}`} data-testid={`coach-badge-${b}`}>{m.label}</span>;
        })}
        {c.years_experience != null && <span className="chip-badge">{c.years_experience}+ {lang === "bg" ? "г." : "yr"}</span>}
      </div>
      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
        <div>
          <div className="label-mono">{lang === "bg" ? "От" : "From"}</div>
          {c.hourly_rate != null ? (
            <div className="font-oswald text-2xl text-pitch text-glow-emerald leading-none mt-0.5">
              €{c.hourly_rate}<span className="text-xs text-white/40 ml-1">/{lang === "bg" ? "ч" : "h"}</span>
            </div>
          ) : (
            <div className="font-oswald text-base text-white/40 leading-none mt-0.5">—</div>
          )}
        </div>
        <span className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5 group-hover:glow-emerald-soft transition-shadow">
          <Sparkles className="w-3.5 h-3.5" />
          {lang === "bg" ? "Резервирай" : "Book"}
        </span>
      </div>
      {c.available_hours && (
        <div className="mt-2 text-xs text-white/40 truncate font-mono">⏱ {c.available_hours}</div>
      )}
    </Link>
  );
}

function AcademyCard({ a, lang }) {
  return (
    <Link
      to={`/academy/${a.id}`}
      className="card-coach p-6 group block relative"
      data-testid={`academy-card-${a.id}`}
    >
      {a.is_pro_academy && (
        <div className="absolute top-3 right-3 chip-badge gold badge-pulse z-10">
          <Sparkles className="w-3 h-3" /> PRO
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-pitch/10 border border-pitch/40 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {a.academy_logo ? (
            <AuthImage src={a.academy_logo} className="w-full h-full object-cover" fallback={<Building2 className="w-7 h-7 text-pitch" />} />
          ) : (
            <Building2 className="w-7 h-7 text-pitch" />
          )}
        </div>
        <div className="flex-1 min-w-0 pr-12">
          <div className="font-oswald uppercase text-xl tracking-tight truncate">
            {a.academy_name || a.name}
          </div>
          {a.city && (
            <div className="text-xs text-white/50 flex items-center gap-1 mt-1.5 font-mono">
              <MapPin className="w-3 h-3 text-pitch" /> {a.city}
            </div>
          )}
          {a.academy_description && (
            <div className="text-xs text-white/55 mt-2 line-clamp-2">{a.academy_description}</div>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {a.academy_size && <span className="chip-badge">{a.academy_size}</span>}
        {(a.badges || []).slice(0, 2).map((b) => (
          <span key={b} className="chip-badge gold">{b.replace("_", " ").toUpperCase()}</span>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
        <div className="flex gap-5">
          <div>
            <div className="label-mono">{lang === "bg" ? "Треньори" : "Coaches"}</div>
            <div className="font-oswald text-2xl text-pitch leading-none mt-0.5">{a.coaches_count || 0}</div>
          </div>
          <div>
            <div className="label-mono">{lang === "bg" ? "Играчи" : "Players"}</div>
            <div className="font-oswald text-2xl text-white leading-none mt-0.5">{a.players_count || 0}</div>
          </div>
        </div>
        <span className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          {lang === "bg" ? "Виж" : "View"}
        </span>
      </div>
    </Link>
  );
}

export default function FindCoaches() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [kind, setKind] = useState("coaches"); // "coaches" | "academies"
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [city, setCity] = useState("");

  const [meta, setMeta] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (city) params.city = city;
      if (kind === "coaches") {
        if (specialty) params.specialty = specialty;
        const { data } = await api.get("/public/coaches", { params });
        // Iter 33 — endpoint now returns {coaches, meta}. Keep tolerant for
        // any legacy callers still expecting an array.
        if (Array.isArray(data)) {
          setList(data); setMeta(null);
        } else {
          setList(data.coaches || []);
          setMeta(data.meta || null);
        }
      } else {
        const { data } = await api.get("/public/academies", { params });
        setList(Array.isArray(data) ? data : (data.academies || data));
        setMeta(null);
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [specialty, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tid = setTimeout(load, 350);
    return () => clearTimeout(tid);
    // eslint-disable-next-line
  }, [q, city]);

  return (
    <div className="min-h-screen pb-16">
      {/* Top bar — works for public + authed */}
      <header className="header-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between p-4">
          <Link to={user ? "/" : "/login"} className="flex items-center">
            <Logo size={26} wordmark />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/coaches-map" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1.5" data-testid="topbar-map">
              <MapIcon className="w-3.5 h-3.5" /> {lang === "bg" ? "Карта" : "Map"}
            </Link>
            <LanguageToggle />
            {user ? (
              <Link to="/" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> {t("dashboard")}
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors">{t("login")}</Link>
                <Link to="/register" className="btn-primary px-3 py-1.5 text-xs">{t("register")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1">// {lang === "bg" ? "Открий" : "Discover"}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
              {kind === "coaches" ? <Trophy className="w-7 h-7 text-pitch" /> : <Building2 className="w-7 h-7 text-pitch" />}
              {kind === "coaches"
                ? (lang === "bg" ? "Намери своя треньор" : "Discover coaches")
                : (lang === "bg" ? "Открий академия" : "Discover academies")}
            </h1>
          </div>
          <div className="inline-flex border border-white/10 p-0.5">
            <button className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-pitch text-white flex items-center gap-1" data-testid="view-grid">
              <LayoutGrid className="w-3 h-3" /> {t("gridView")}
            </button>
            <Link to="/coaches-map" className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white/55 hover:text-pitch flex items-center gap-1" data-testid="view-map">
              <MapIcon className="w-3 h-3" /> {t("mapView")}
            </Link>
          </div>
        </div>

        {/* Type toggle — Coaches | Academies */}
        <div className="inline-flex border border-white/10 p-0.5 self-start" data-testid="discover-toggle">
          <button
            onClick={() => setKind("coaches")}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors ${kind === "coaches" ? "bg-pitch text-white" : "text-white/55 hover:text-pitch"}`}
            data-testid="kind-coaches"
          >
            <Users className="w-3 h-3" /> {lang === "bg" ? "Треньори" : "Coaches"}
          </button>
          <button
            onClick={() => setKind("academies")}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors ${kind === "academies" ? "bg-pitch text-white" : "text-white/55 hover:text-pitch"}`}
            data-testid="kind-academies"
          >
            <Building2 className="w-3 h-3" /> {lang === "bg" ? "Академии" : "Academies"}
          </button>
        </div>

        {/* Filters */}
        <div className="card-sharp p-4 space-y-3 animate-fade-up" style={{ opacity: 0 }}>
          <div className={`grid grid-cols-1 ${kind === "coaches" ? "md:grid-cols-3" : "md:grid-cols-2"} gap-3`}>
            <div className="relative">
              <SearchIcon className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={kind === "coaches" ? (lang === "bg" ? "Търси име..." : "Search by name...") : (lang === "bg" ? "Търси академия..." : "Search academy...")}
                className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none"
                data-testid="find-search"
              />
            </div>
            <div className="relative">
              <MapPin className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={lang === "bg" ? "Град" : "City"}
                className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none"
                data-testid="find-city"
              />
            </div>
            {kind === "coaches" && (
              <div className="relative">
                <Filter className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none"
                  data-testid="find-specialty"
                >
                  <option value="">{lang === "bg" ? "Всички специалности" : "All specialties"}</option>
                  {SPECIALTIES.map((s) => (
                    <option key={s.id} value={s.id}>{lang === "bg" ? s.label_bg : s.label_en}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {kind === "coaches" && (
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSpecialty(specialty === s.id ? "" : s.id)}
                  className={`chip-badge ${specialty === s.id ? "" : "opacity-60 hover:opacity-100"}`}
                  style={specialty === s.id ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                  data-testid={`specialty-pill-${s.id}`}
                >
                  {lang === "bg" ? s.label_bg : s.label_en}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="pitch-loader"><div className="ball" /></div></div>
        ) : list.length === 0 ? (
          <div className="card-sharp p-12 text-center text-white/30 font-mono text-xs tracking-widest">
            {kind === "coaches"
              ? (lang === "bg" ? "Няма намерени треньори" : "No coaches found")
              : (lang === "bg" ? "Няма намерени академии" : "No academies found")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={kind === "coaches" ? "coaches-grid" : "academies-grid"}>
              {list.map((c, i) => (
                <div key={c.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}>
                  {kind === "coaches" ? <CoachCard c={c} lang={lang} /> : <AcademyCard a={c} lang={lang} />}
                </div>
              ))}
            </div>
            {/* Iter 33 — Free-tier upgrade teaser when results truncated */}
            {kind === "coaches" && meta?.truncated && (
              <div className="surface-elevated p-6 sm:p-8 mt-6 border border-pitch/30 bg-pitch/[0.03] text-center" data-testid="discover-upgrade-teaser">
                <div className="label-mono text-pitch">// {lang === "bg" ? "Надстрой за повече" : "Upgrade for more"}</div>
                <div className="font-oswald uppercase text-xl sm:text-2xl tracking-tight mt-2">
                  {lang === "bg"
                    ? `Виждаш ${meta.shown} от ${meta.total} треньора`
                    : `Showing ${meta.shown} of ${meta.total} coaches`}
                </div>
                <p className="text-xs text-white/55 mt-2 max-w-md mx-auto">
                  {lang === "bg"
                    ? "Free акаунтите виждат само топ резултатите. Pro отключва пълния списък + неограничено търсене."
                    : "Free accounts see only top results. Pro unlocks the full list + unlimited search."}
                </p>
                <Link to="/upgrade" className="btn-primary px-5 py-2.5 text-xs mt-5 inline-flex items-center gap-1.5" data-testid="discover-upgrade-cta">
                  <Sparkles className="w-3.5 h-3.5" />{lang === "bg" ? "Стани Pro" : "Go Pro"}
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
